// Tickets in Settings (packages/shared domain/ticket.ts): send one to the administrators (a problem, a
// request for a feature or for access, a correction to data, and how urgent it is, with screenshots if
// they help: components/TicketShots.tsx), and follow your own: open, accepted or denied with the
// administrator's reply, or withdrawn. The user menu's Tickets opens this
// card (/settings#tickets). The administrators answer in Administration › Tickets (pages/admin/Tickets.tsx).
import { useMemo, useState } from 'react';
import {
  TICKET_CATEGORIES, TICKET_CATEGORY_LABEL, TICKET_MAX_FILES, TICKET_PRIORITY_LABEL, TICKET_STATUS_LABEL,
  type Ticket, type TicketCategory, type TicketPriority, type TicketStatus,
} from '@technohull/shared';
import { uploadTicketFile, useCreateTicket, useMyTickets, useRefreshTickets, useWithdrawTicket } from '../features/tickets.js';
import { ShotPicker, TicketShots, usePastedPictures, useShotPicker } from './TicketShots.js';
import { useNameResolver } from '../features/directory.js';
import { useAuth } from '../auth/AuthContext.js';
import { NAV } from '../app/nav.js';
import { useDateFormatter } from '../lib/format.js';
import { InlineError, Modal, Pill, Segmented } from './ui.js';

/** a ticket's status in the shared pill colours */
export const TICKET_STATUS_KIND: Record<TicketStatus, string> = { open: 'info', accepted: 'ok', denied: 'bad', withdrawn: '' };
export function TicketStatusPill({ status }: { status: TicketStatus }) {
  return <Pill kind={TICKET_STATUS_KIND[status]}>{TICKET_STATUS_LABEL[status]}</Pill>;
}

const SHOWN = 5;

/** your tickets, and the button to send a new one */
export function MyTickets() {
  const q = useMyTickets();
  const withdraw = useWithdrawTicket();
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const [adding, setAdding] = useState(false);
  const [all, setAll] = useState(false);
  const [sent, setSent] = useState<{ ticket: Ticket; failed: number } | null>(null);
  const tickets = q.data?.tickets ?? [];
  const shown = all ? tickets : tickets.slice(0, SHOWN);
  const open = tickets.filter((t) => t.status === 'open').length;

  return (
    <div className="settings-row tk-mine">
      <p className="settings-hint">
        Something not working, or something you need? Send a ticket to the administrators. You’re told on the bell and by email when they accept or deny it.
      </p>
      <div className="tk-mine-bar">
        <button type="button" className="btn btn-primary" onClick={() => { setSent(null); setAdding(true); }}>New ticket</button>
        {tickets.length > 0 && <span className="muted">{open ? `${open} open · ` : ''}{tickets.length} sent</span>}
      </div>
      {sent && (
        <p className={`tk-sent${sent.failed ? ' is-warn' : ''}`} role="status">
          Ticket {sent.ticket.ref} sent. The administrators have been told.
          {sent.failed > 0 && <> {sent.failed === 1 ? 'One screenshot' : `${sent.failed} screenshots`} didn’t go on: add {sent.failed === 1 ? 'it' : 'them'} to the ticket below.</>}
        </p>
      )}
      {q.error != null && <InlineError error={q.error} />}
      {withdraw.isError && <InlineError error={withdraw.error} onDismiss={() => withdraw.reset()} />}
      {tickets.length > 0 && (
        <ul className="tk-list" aria-label="Your tickets">
          {shown.map((t) => (
            <li key={t.id} className={`tk-item is-${t.status}`}>
              <div className="tk-item-top">
                <span className="tk-ref">{t.ref}</span>
                <TicketStatusPill status={t.status} />
                {t.priority === 'urgent' && <Pill kind="warn">Urgent</Pill>}
                <span className="tk-when">{df.dateTime(t.createdAt, { year: 'auto' })}</span>
              </div>
              <div className="tk-subject">{t.subject}</div>
              <div className="tk-meta">{[TICKET_CATEGORY_LABEL[t.category], t.page].filter(Boolean).join(' · ')}</div>
              <TicketShots ticket={t} editable={t.status === 'open'} />
              {(t.status === 'accepted' || t.status === 'denied') && (
                <div className={`tk-reply is-${t.status}`}>
                  <span className="tk-reply-by">{TICKET_STATUS_LABEL[t.status]} by {nameOf(t.decidedBy) || 'an administrator'} · {df.dateTime(t.decidedAt, { year: 'auto' })}</span>
                  {t.reply && <span className="tk-reply-text">{t.reply}</span>}
                </div>
              )}
              {t.status === 'open' && (
                <button
                  type="button"
                  className="btn btn-sm btn-ghost tk-withdraw"
                  disabled={withdraw.isPending && withdraw.variables === t.id}
                  onClick={() => withdraw.mutate(t.id)}
                >Withdraw</button>
              )}
            </li>
          ))}
        </ul>
      )}
      {tickets.length > SHOWN && (
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => setAll((a) => !a)}>
          {all ? 'Show the latest only' : `Show all ${tickets.length}`}
        </button>
      )}
      {adding && <NewTicket onClose={() => setAdding(false)} onSent={(ticket, failed) => { setSent({ ticket, failed }); setAdding(false); }} />}
    </div>
  );
}

/** the form for a new ticket. `onSent` hears how many screenshots didn't go on. */
export function NewTicket({ onClose, onSent }: { onClose: () => void; onSent: (t: Ticket, failed: number) => void }) {
  const create = useCreateTicket();
  const refresh = useRefreshTickets();
  const picker = useShotPicker(TICKET_MAX_FILES);
  // a screenshot pasted anywhere while the form is open goes on the ticket
  usePastedPictures(true, picker.add);
  const [uploading, setUploading] = useState(false);
  const { can } = useAuth();
  const [category, setCategory] = useState<TicketCategory>('problem');
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [subject, setSubject] = useState('');
  const [details, setDetails] = useState('');
  const [page, setPage] = useState('');
  // the pages you can open, offered for "Page or feature"
  const pages = useMemo(() => {
    const out = new Set<string>();
    for (const g of NAV) for (const it of g.items) {
      for (const x of [it, ...(it.children ?? [])]) if (x.path && !x.hidden && (!x.perm || can(x.perm))) out.add(x.label);
    }
    return [...out];
  }, [can]);
  const ok = subject.trim().length >= 3 && !picker.busy;
  const busy = create.isPending || uploading;
  // the ticket first, then its screenshots one by one; one that doesn't go on can be added from the list
  const send = () => {
    if (!ok || busy) return;
    create.mutate({ category, priority, subject: subject.trim(), details: details.trim(), page: page.trim() }, {
      onSuccess: async (res) => {
        let failed = 0;
        if (picker.ready.length) {
          setUploading(true);
          for (const s of picker.ready) await uploadTicketFile(res.ticket.id, s.prepared!).catch(() => { failed++; });
          await refresh();
        }
        onSent(res.ticket, failed);
      },
    });
  };

  return (
    <Modal
      title="New ticket"
      onClose={onClose}
      width={560}
      footer={(
        <>
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" disabled={!ok || busy} onClick={send}>
            {uploading ? 'Adding screenshots…' : create.isPending ? 'Sending…' : picker.busy ? 'Preparing screenshots…' : 'Send ticket'}
          </button>
        </>
      )}
    >
      <form className="tk-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <fieldset className="tk-kinds">
          <legend className="field-label">What is it about?</legend>
          {TICKET_CATEGORIES.map((c) => (
            <label key={c} className={`tk-kind${category === c ? ' on' : ''}`}>
              <input type="radio" name="tk-category" value={c} checked={category === c} onChange={() => setCategory(c)} />
              <span>{TICKET_CATEGORY_LABEL[c]}</span>
            </label>
          ))}
        </fieldset>
        <label className="tk-field">
          <span className="field-label">In a few words</span>
          <input className="field" value={subject} maxLength={120} autoFocus placeholder="e.g. The Washing Plan printout cuts off the last day" onChange={(e) => setSubject(e.target.value)} />
        </label>
        <label className="tk-field">
          <span className="field-label">Details <span className="muted">(what happened, what you expected, what you need)</span></span>
          <textarea className="field" rows={5} value={details} maxLength={4000} onChange={(e) => setDetails(e.target.value)} />
        </label>
        <div className="tk-row">
          <label className="tk-field">
            <span className="field-label">Page or feature <span className="muted">(optional)</span></span>
            <input className="field" list="tk-pages" value={page} maxLength={120} placeholder="e.g. Washing Plan" onChange={(e) => setPage(e.target.value)} />
            <datalist id="tk-pages">{pages.map((p) => <option key={p} value={p} />)}</datalist>
          </label>
          <div className="tk-field">
            <span className="field-label" id="tk-priority">How urgent</span>
            <Segmented<TicketPriority>
              value={priority}
              onChange={setPriority}
              ariaLabel="How urgent"
              options={(['low', 'normal', 'urgent'] as const).map((p) => ({ value: p, label: TICKET_PRIORITY_LABEL[p] }))}
            />
          </div>
        </div>
        <div className="tk-field">
          <span className="field-label">Screenshots <span className="muted">(optional, up to {TICKET_MAX_FILES})</span></span>
          <ShotPicker picker={picker} room={TICKET_MAX_FILES} />
        </div>
        {create.isError && <InlineError error={create.error} onDismiss={() => create.reset()} />}
      </form>
    </Modal>
  );
}
